import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box } from '@chakra-ui/react';
import {
  NOTIFICATION_TYPE,
  SUBSCRIPTION_STATUS_TYPE,
} from '../../../../../constants/benchat';
import { isInAppBrowser } from '../../../../../service/browserService';
import benchatService from '../../../../../service/benchatService';
import { notify as pnotifyNotify } from '../../../../../service/pnotifyService';
import trackerService from '../../../../../service/trackerService';
import {
  EVENT_NAME_FOR_SUBSCRIPTION,
  EVENT_CATEGORY_FOR_SUBSCRIPTION,
  EVENT_PROPERTY_FOR_SUBSCRIPTION,
  EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION,
} from '../../../../../shop/constants/tracker';
import getPopupWindowSize from '../../../../../utils/getPopupWindowSize';
import { CheckCircle, Line } from '../../../../context/Chakra/Icons';
import { SIGNUP_LOGIN_E2E_TEST_IDS } from '../../../constants/e2eTestId';
import { UseBenchatSubscriptionsPropTypes } from '../../../utils/propTypes';

function LineSubscription({ platformChannelId, benchat }) {
  const { t } = useTranslation();

  const refPopupWindow = useRef();

  useEffect(() => {
    switch (benchat.subscriptionStatus) {
      case SUBSCRIPTION_STATUS_TYPE.INITIAL:
      case SUBSCRIPTION_STATUS_TYPE.LOADING:
        break;
      case SUBSCRIPTION_STATUS_TYPE.SUCCESS:
        pnotifyNotify(t('Subscription - connection notification - success'));
        break;
      case SUBSCRIPTION_STATUS_TYPE.NO_RECORD:
      case SUBSCRIPTION_STATUS_TYPE.UNKNOWN_ERROR:
        pnotifyNotify(
          t('Subscription - LINE - connection notification - failure'),
          {
            customClass: 'error',
            icon: 'fa fa-exclamation-triangle',
          },
        );
        break;
      default:
        break;
    }
  }, [benchat.subscriptionStatus, t]);

  const handleSubscription = async () => {
    if (isInAppBrowser()) {
      const url = benchatService.getLineMessageSubscriptionRedirectToUri(
        platformChannelId,
        benchat.userRef.current,
        NOTIFICATION_TYPE.MARKETING,
      );
      window.location.replace(
        `${url}&shopRedirectUri=${window.location.pathname}`,
      );
    } else {
      refPopupWindow.current = window.open(
        benchatService.getLineMessageSubscriptionPopupUri(
          platformChannelId,
          benchat.userRef.current,
          NOTIFICATION_TYPE.MARKETING,
        ),
        'LINE LOGIN',
        getPopupWindowSize(),
      );
      const timer = setInterval(() => {
        if (refPopupWindow.current.closed) {
          clearInterval(timer);
          benchat.startPolling();
        }
      }, 1000);
    }
  };

  const handleClick = () => {
    if (benchat.hasSubscribed) return;
    handleSubscription();

    trackerService.track({
      type: EVENT_NAME_FOR_SUBSCRIPTION.MEMBER_SUBSCRIPTION_BUTTON_CLICK,
      data: {
        event_category: EVENT_CATEGORY_FOR_SUBSCRIPTION.MEMBER_SUBSCRIPTION,
        [EVENT_PROPERTY_FOR_SUBSCRIPTION.SUBSCRIPTION_TYPE]:
          EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION.PROMOTION_NOTIFICATION,
        [EVENT_PROPERTY_FOR_SUBSCRIPTION.SUBSCRIPTION_PATH]:
          EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION.SIGN_UP_PAGE,
        [EVENT_PROPERTY_FOR_SUBSCRIPTION.THIRD_PARTY]:
          EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION.LINE,
      },
    });
  };

  return (
    <Box
      data-e2e-id={SIGNUP_LOGIN_E2E_TEST_IDS.SIGNUP_SUBSCRIPTION_LINE_BUTTON}
      display="inline-flex"
      alignItems="center"
      bg="white"
      borderRadius="4px"
      borderWidth="1px"
      p={['8px', '10px', '7px']}
      color="#06C755"
      fontSize="16px"
      fontWeight={500}
      fontFamily="Helvetica, Arial, sans-serif"
      lineHeight={1}
      cursor="pointer"
      onClick={handleClick}
    >
      {benchat.hasSubscribed ? (
        <CheckCircle w="16px" h="16px" mr="6px" ml="2px" color="#06C755" />
      ) : (
        <Line w="16px" h="16px" mr="6px" ml="2px" />
      )}
      {t('Subscription - LINE - subscribe')}
    </Box>
  );
}

LineSubscription.propTypes = {
  platformChannelId: PropTypes.string.isRequired,
  benchat: UseBenchatSubscriptionsPropTypes,
};

export default LineSubscription;
